import {
  BulbOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  TagsOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Select,
  Spin,
  Upload,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import mealApi from '../../api/meal.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/product.module.scss'
import '../../styles/program.scss'
import { mealCategory } from './modeal'

const MealDetailPage = () => {
  const [mealData, setMealData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editData, setEditData] = useState({
    mealCategoryId: null,
    mealName: '',
    mealImage: null,
  })
  const navigate = useNavigate()
  const { mealId } = useParams()

  useEffect(() => {
    if (mealId) {
      getMealDetail()
    }
  }, [mealId])

  const getMealDetail = async () => {
    setIsLoading(true)
    try {
      console.log('mealId', mealId)
      const res = await mealApi.getMealDetail(mealId)
      if (res.status === 200) {
        setMealData(res.data)
        // 수정용 데이터 초기화
        setEditData({
          mealCategoryId: res.data.mealCategoryId,
          mealName: res.data.mealName,
          mealImage: null,
        })
      }
    } catch (error) {
      console.error('음식 상세 조회 오류:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getCategoryName = (categoryId) => {
    const category = mealCategory.find((cat) => cat.id === categoryId)
    return category ? category.categoryName : '-'
  }

  const handleEditDataChange = (key, value) => {
    setEditData((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleImageChange = (info) => {
    if (info.fileList && info.fileList.length > 0) {
      setEditData((prev) => ({
        ...prev,
        mealImage: info.fileList[0],
      }))
    } else {
      setEditData((prev) => ({
        ...prev,
        mealImage: null,
      }))
    }
  }

  const handleEdit = () => {
    setIsEditMode(true)
  }

  const handleCancel = () => {
    setIsEditMode(false)
    // 원래 데이터로 복원
    setEditData({
      mealCategoryId: mealData.mealCategoryId,
      mealName: mealData.mealName,
      mealImage: null,
    })
  }

  const handleUpdateMeal = async () => {
    setIsLoading(true)
    try {
      const formData = new FormData()
      formData.append('mealId', mealId)
      formData.append('mealCategoryId', editData.mealCategoryId)
      formData.append('mealName', editData.mealName)

      // 이미지가 있을 때만 file 추가
      const file = editData.mealImage?.originFileObj
      if (file) {
        formData.append('file', file)
      }

      const res = await mealApi.updateMeal(formData)
      console.log('res', res)

      if (res.status === 200) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: '음식이 성공적으로 수정되었습니다.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })
        setIsEditMode(false)
        await getMealDetail() // 데이터 다시 로드
      }
    } catch (error) {
      console.error('음식 수정 오류:', error)
      const errorDescription =
        error.response?.data?.detail?.[0]?.description ||
        '음식 수정 중 오류가 발생했습니다.'
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: errorDescription,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid = () => {
    return editData.mealCategoryId && editData.mealName
  }

  if (!mealData) {
    return (
      <div style={{ width: '100%', paddingBottom: '8rem' }}>
        <ContentHeader title="음식 상세 정보" />
        <div style={{ textAlign: 'center', padding: '50px' }}>
          음식 정보를 찾을 수 없습니다.
        </div>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', paddingBottom: '8rem' }}>
      <Spin spinning={isLoading}>
        <ContentHeader title={isEditMode ? '음식 수정' : '음식 상세 정보'} />

        {/* 기본 정보 */}
        <Card
          title={
            <>
              <TagsOutlined style={{ marginRight: 8 }} />
              기본 정보
            </>
          }
          style={{ marginBottom: 20 }}
        >
          {isEditMode ? (
            <>
              <Form.Item
                label="카테고리"
                labelAlign="left"
                colon={false}
                labelCol={{
                  flex: '0 0 120px',
                  style: {
                    minWidth: 120,
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                  },
                }}
                wrapperCol={{ flex: '1 1 auto' }}
                required
                style={{ marginBottom: 20 }}
              >
                <Select
                  style={{
                    width: '25vw',
                    minWidth: 200,
                    flexShrink: 0,
                  }}
                  placeholder="식단 카테고리를 선택하세요"
                  options={mealCategory.map((item) => ({
                    label: item.categoryName,
                    value: item.id,
                  }))}
                  value={editData.mealCategoryId}
                  onChange={(value) =>
                    handleEditDataChange('mealCategoryId', value)
                  }
                />
              </Form.Item>
              <Form.Item
                label="음식명"
                labelAlign="left"
                colon={false}
                labelCol={{
                  flex: '0 0 120px',
                  style: {
                    minWidth: 120,
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                  },
                }}
                wrapperCol={{ flex: '1 1 auto' }}
                required
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Input
                    maxLength={50}
                    style={{
                      width: '25vw',
                      minWidth: 200,
                      flexShrink: 0,
                    }}
                    value={editData.mealName}
                    onChange={(e) =>
                      handleEditDataChange('mealName', e.target.value)
                    }
                  />
                </div>
                <div className={`${['product-name-count-wrap']}`}>
                  <span className={`${['product-name-count']}`}>
                    {editData.mealName.length}
                  </span>
                  /50
                </div>
              </Form.Item>
            </>
          ) : (
            <Descriptions
              column={1}
              labelStyle={{ width: '120px', fontWeight: 500 }}
            >
              <Descriptions.Item label="음식 ID">
                {mealData.id}
              </Descriptions.Item>
              <Descriptions.Item label="카테고리">
                {getCategoryName(mealData.mealCategoryId)}
              </Descriptions.Item>
              <Descriptions.Item label="음식명">
                {mealData.mealName}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        {/* 음식 이미지 */}
        <Card
          title={
            <>
              <PictureOutlined style={{ marginRight: 8 }} />
              음식 이미지
            </>
          }
          style={{ marginBottom: 20 }}
        >
          {isEditMode ? (
            <div style={{ width: '25vw', minWidth: 200 }}>
              <Upload
                name="mealImage"
                maxCount={1}
                listType="picture"
                beforeUpload={() => false}
                onChange={handleImageChange}
                fileList={editData.mealImage ? [editData.mealImage] : []}
                style={{
                  width: '100%',
                  minWidth: 200,
                }}
                accept="image/svg+xml"
              >
                <Button
                  style={{
                    width: '200px',
                    height: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <UploadOutlined style={{ fontSize: '24px' }} />
                  <span>
                    이미지 선택&nbsp;
                    <p style={{ color: 'gray' }}>(36x36 svg 필수)</p>
                  </span>
                </Button>
              </Upload>
              {mealData.mealImageLink && !editData.mealImage && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ marginBottom: 8, fontWeight: 500 }}>
                    현재 이미지:
                  </p>
                  <img
                    src={mealData.mealImageLink}
                    alt={mealData.mealName}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #d9d9d9',
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '20px',
              }}
            >
              {mealData.mealImageLink ? (
                <img
                  src={mealData.mealImageLink}
                  alt={mealData.mealName}
                  style={{
                    width: '200px',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '200px',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #d9d9d9',
                    borderRadius: '8px',
                    color: '#999',
                  }}
                >
                  이미지 없음
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 혈당 관련 정보 (수정 모드가 아닐 때만 표시) */}
        {!isEditMode && (
          <Card
            title={
              <>
                <HeartOutlined style={{ marginRight: 8 }} />
                혈당 관련 정보
              </>
            }
            style={{ marginBottom: 20 }}
          >
            <Card
              type="inner"
              title={
                <>
                  <HeartOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  좋은 혈당 반응
                </>
              }
              style={{ marginBottom: 16 }}
            >
              <p style={{ margin: 0, lineHeight: '1.6' }}>
                {mealData.bloodSugarGoodResponseReason || '정보 없음'}
              </p>
            </Card>

            <Card
              type="inner"
              title={
                <>
                  <WarningOutlined
                    style={{ marginRight: 8, color: '#ff7875' }}
                  />
                  나쁜 혈당 반응
                </>
              }
              style={{ marginBottom: 16 }}
            >
              <p style={{ margin: 0, lineHeight: '1.6' }}>
                {mealData.bloodSugarBadResponseReason || '정보 없음'}
              </p>
            </Card>

            <Card
              type="inner"
              title={
                <>
                  <BulbOutlined style={{ marginRight: 8, color: '#faad14' }} />
                  혈당 관리 팁
                </>
              }
            >
              <p style={{ margin: 0, lineHeight: '1.6' }}>
                {mealData.bloodSugarManagementTip || '정보 없음'}
              </p>
            </Card>
          </Card>
        )}

        {/* 영양 정보 (수정 모드가 아닐 때만 표시) */}
        {!isEditMode && (
          <Card
            title={
              <>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                영양 정보
              </>
            }
            style={{ marginBottom: 20 }}
          >
            <Descriptions column={2} labelStyle={{ fontWeight: 500 }}>
              <Descriptions.Item label="탄수화물">
                {mealData.carbohydrates}g
              </Descriptions.Item>
              <Descriptions.Item label="단백질">
                {mealData.protein}g
              </Descriptions.Item>
              <Descriptions.Item label="지방">
                {mealData.fat}g
              </Descriptions.Item>
              <Descriptions.Item label="당류">
                {mealData.sugars}g
              </Descriptions.Item>
              <Descriptions.Item label="식이섬유">
                {mealData.dietaryFiber}g
              </Descriptions.Item>
              <Descriptions.Item label="포화지방">
                {mealData.saturatedFat}g
              </Descriptions.Item>
              <Descriptions.Item label="불포화지방">
                {mealData.unsaturatedFat}g
              </Descriptions.Item>
              <Descriptions.Item label="탄수화물 비율">
                {mealData.percentOfCarbohydrates}%
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* 하단 버튼 */}
        <div className={`${styles['product-register-fixed-footer']}`}>
          {isEditMode ? (
            <>
              <Button
                className={`${styles['product-register-button']} ${styles.save}`}
                disabled={!isFormValid()}
                onClick={handleUpdateMeal}
                loading={isLoading}
              >
                저장하기
              </Button>
              <Button
                className={`${styles['product-register-button']}`}
                onClick={handleCancel}
                style={{ marginLeft: 16 }}
              >
                취소
              </Button>
            </>
          ) : (
            <Button
              className={`${styles['product-register-button']} ${styles.save}`}
              onClick={handleEdit}
            >
              수정하기
            </Button>
          )}
        </div>
      </Spin>
    </div>
  )
}

export default MealDetailPage
